import React, { useState, useEffect } from 'react';
import { Box, Typography, useMediaQuery, Container, Fab, IconButton } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import parse, { domToReact } from 'html-react-parser';
import { BASE_URL } from '../network/endpoints';
import instId from '../network/instituteId';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShareIcon from '@mui/icons-material/Share';
import BookmarkIcon from '@mui/icons-material/Bookmark';

// Add CSS animations
const globalStyles = `
    @keyframes fadeInUp {
        from {
            opacity: 0;
            transform: translateY(50px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
    
    @keyframes slideInLeft {
        from {
            opacity: 0;
            transform: translateX(-80px);
        }
        to {
            opacity: 1;
            transform: translateX(0);
        }
    }
    
    @keyframes pulse {
        0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(102, 126, 234, 0.7);
        }
        50% {
            transform: scale(1.05);
            box-shadow: 0 0 0 10px rgba(102, 126, 234, 0);
        }
    }
    
    @keyframes float {
        0%, 100% {
            transform: translateY(0px) rotate(0deg);
        }
        50% {
            transform: translateY(-15px) rotate(5deg);
        }
    }
    
    @keyframes shimmer {
        0% {
            background-position: -1200px 0;
        }
        100% {
            background-position: 1200px 0;
        }
    }
    
    @keyframes bounceIn {
        0% {
            opacity: 0;
            transform: scale(0.2) rotate(-45deg);
        }
        50% {
            opacity: 1;
            transform: scale(1.15) rotate(10deg);
        }
        100% {
            opacity: 1;
            transform: scale(1) rotate(0deg);
        }
    }
    
    @keyframes glow {
        0%, 100% {
            box-shadow: 0 0 20px rgba(102, 126, 234, 0.3);
        }
        50% {
            box-shadow: 0 0 40px rgba(102, 126, 234, 0.6);
        }
    }
    
    @keyframes morphBackground {
        0%, 100% {
            border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%;
        }
        50% {
            border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%;
        }
    }
`;

// Inject styles
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = globalStyles;
    document.head.appendChild(styleSheet);
}

const BlogSection = () => {

    function linkifyHtml(htmlString) {
        const urlRegex = /((https?:\/\/[^\s<]+))/g;
        return htmlString.replace(urlRegex, (url) => {
            if (htmlString.includes(`<a href="${url}`)) return url;
            return `<a href="${url}">${url}</a>`;
        });
    }
    const options = {
        replace: (domNode) => {
            if (domNode.name === 'a' && domNode.attribs?.href) {
                const href = domNode.attribs.href;
                const anchorText = domNode.children?.[0]?.data?.toLowerCase?.() || '';
                const isDownloadLink =
                    anchorText.includes('click here to download') ||
                    anchorText.includes('click to download');

                const handleDownload = async (e) => {
                    e.preventDefault();

                    try {
                        const response = await fetch(href);
                        const blob = await response.blob();

                        const fileName = href.split('/').pop().split('?')[0] || 'downloaded_file';
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = fileName;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                    } catch (error) {
                        console.error('Download failed:', error);
                        window.open(href, '_blank'); // fallback
                    }
                };

                return isDownloadLink ? (
                    <a
                        href={href}
                        onClick={handleDownload}
                        style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
                    >
                        {domToReact(domNode.children, options)}
                    </a>
                ) : (
                    <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#1976d2', textDecoration: 'underline' }}
                    >
                        {domToReact(domNode.children, options)}
                    </a>
                );
            }

            if (domNode.name === 'img' && domNode.attribs?.src) {
                return (
                    <Box sx={{
                        my: 4,
                        borderRadius: '20px',
                        overflow: 'hidden',
                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(102, 126, 234, 0.05) 100%)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(102, 126, 234, 0.2)',
                        boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
                        animation: 'fadeInUp 0.8s ease-out',
                    }}>
                        <img
                            src={domNode.attribs.src}
                            alt={domNode.attribs.alt || ''}
                            style={{ 
                                width: '100%', 
                                maxWidth: '100%', 
                                height: 'auto',
                                borderRadius: '20px',
                                transition: 'transform 0.3s ease',
                            }}
                            onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
                            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                        />
                    </Box>
                );
            }
        },
    };

    const isMobile = useMediaQuery("(min-width:600px)");
    const location = useLocation();
    const navigate = useNavigate();
    const blogsData = location?.state?.blogData;
    const rawHtml = blogsData?.blog?.blog || '';
    const htmlWithLinks = linkifyHtml(rawHtml);
    const parsedContent = parse(rawHtml, options);
    const [Endpoints, setEndpoints] = useState('');

    useEffect(() => {
        if (!blogsData?.id) {
            navigate('/free-resouces')
        }
    }, [blogsData])

    useEffect(() => {
        window.scrollTo(0, 0);
        getInstituteDetail();
    }, []);


    const getInstituteDetail = async () => {
        try {
            let requestOptions = {
                // headers: { "X-Auth": token },
                withCredentials: false,
            };
            const response = await axios.get(
                BASE_URL + "/getMetaData/fetch-institute/" + instId,
                requestOptions
            );
            if (response?.data?.errorCode === 0) {
                setEndpoints(response?.data?.instituteTechSetting?.mediaUrl)
                // Endpoints = response?.data?.instituteTechSetting?.mediaUrl
            };
        } catch (error) {
            console.log(error);
        }
    };

    // console.log('blogsData', blogsData);

    return (
        <React.Fragment>
            {/* Modern Background with Gradient */}
            <Box sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #667eea 100%)',
                minHeight: '100vh',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '-50%',
                    left: '-50%',
                    width: '200%',
                    height: '200%',
                    background: 'radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(118, 75, 162, 0.2) 0%, transparent 50%), radial-gradient(circle at 50% 50%, rgba(102, 126, 234, 0.1) 0%, transparent 70%)',
                    animation: 'morphBackground 15s ease-in-out infinite, float 8s ease-in-out infinite',
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.03) 50%, transparent 70%)',
                    backgroundSize: '400% 400%',
                    animation: 'shimmer 4s linear infinite',
                },
            }}>
                {/* Floating Background Elements */}
                <Box sx={{
                    position: 'absolute',
                    top: '10%',
                    right: '10%',
                    width: '100px',
                    height: '100px',
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                    borderRadius: '50%',
                    animation: 'float 6s ease-in-out infinite',
                    zIndex: 0,
                }}>
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '60%',
                        height: '60%',
                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)',
                        borderRadius: '50%',
                        animation: 'pulse 3s ease-in-out infinite',
                    }} />
                </Box>

                <Box sx={{
                    position: 'absolute',
                    bottom: '20%',
                    left: '5%',
                    width: '80px',
                    height: '80px',
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)',
                    borderRadius: '50%',
                    animation: 'float 8s ease-in-out infinite reverse',
                    zIndex: 0,
                }} />

                {/* Back Button */}
                <Box sx={{ 
                    position: 'fixed',
                    top: { xs: 20, sm: 30 },
                    left: { xs: 20, sm: 30 },
                    zIndex: 1000,
                }}>
                    <IconButton 
                        onClick={() => navigate('/free-resouces')}
                        sx={{
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 100%)',
                            backdropFilter: 'blur(20px)',
                            border: '2px solid rgba(255,255,255,0.3)',
                            color: 'rgba(255,255,255,0.9)',
                            width: 56,
                            height: 56,
                            boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                background: 'linear-gradient(135deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.25) 100%)',
                                transform: 'translateY(-3px) scale(1.05)',
                                boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                            },
                        }}
                    >
                        <ArrowBackIcon sx={{ fontSize: '1.5rem' }} />
                    </IconButton>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ 
                    position: 'fixed',
                    top: { xs: 20, sm: 30 },
                    right: { xs: 20, sm: 30 },
                    zIndex: 1000,
                    display: 'flex',
                    gap: 2,
                }}>
                    <IconButton 
                        onClick={() => {
                            if (navigator.share) {
                                navigator.share({
                                    title: blogsData?.title,
                                    text: 'Check out this amazing resource!',
                                    url: window.location.href,
                                });
                            }
                        }}
                        sx={{
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 100%)',
                            backdropFilter: 'blur(20px)',
                            border: '2px solid rgba(255,255,255,0.3)',
                            color: 'rgba(255,255,255,0.9)',
                            width: 50,
                            height: 50,
                            boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                background: 'linear-gradient(135deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.25) 100%)',
                                transform: 'translateY(-3px) scale(1.05)',
                                boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                            },
                        }}
                    >
                        <ShareIcon sx={{ fontSize: '1.2rem' }} />
                    </IconButton>

                    <IconButton 
                        sx={{
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 100%)',
                            backdropFilter: 'blur(20px)',
                            border: '2px solid rgba(255,255,255,0.3)',
                            color: 'rgba(255,255,255,0.9)',
                            width: 50,
                            height: 50,
                            boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                background: 'linear-gradient(135deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.25) 100%)',
                                transform: 'translateY(-3px) scale(1.05)',
                                boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                            },
                        }}
                    >
                        <BookmarkIcon sx={{ fontSize: '1.2rem' }} />
                    </IconButton>
                </Box>

                {/* Main Content Container */}
                <Container 
                    maxWidth={false}
                    sx={{ 
                        pt: { xs: 12, sm: 15 },
                        pb: { xs: 6, sm: 8 },
                        px: { xs: 2, sm: 3, md: 4 },
                        position: 'relative',
                        zIndex: 1,
                        maxWidth: '100%',
                    }}
                >
                    {/* Modern Blog Card */}
                    <Box sx={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)',
                        backdropFilter: 'blur(30px)',
                        border: '2px solid rgba(255,255,255,0.8)',
                        borderRadius: '30px',
                        overflow: 'hidden',
                        boxShadow: '0 30px 80px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                        animation: 'fadeInUp 1s ease-out',
                        position: 'relative',
                        mx: 'auto',
                        maxWidth: '1200px',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
                            animation: 'shimmer 3s linear infinite',
                            zIndex: 0,
                        },
                    }}>
                        {/* Blog Header Image */}
                        {blogsData?.thumb && (
                            <Box sx={{ 
                                position: 'relative',
                                overflow: 'hidden',
                                height: { xs: '200px', sm: '300px', md: '400px' },
                                borderRadius: '25px 25px 0 0',
                            }}>
                                <img 
                                    src={Endpoints + blogsData?.thumb} 
                                    alt={blogsData?.title}
                                    style={{ 
                                        width: "100%", 
                                        height: "100%",
                                        objectFit: "cover",
                                        objectPosition: "center",
                                        transition: 'transform 0.6s ease',
                                        filter: 'brightness(1.1) contrast(1.1)',
                                    }} 
                                />
                                {/* Image Overlay */}
                                <Box sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    height: '50%',
                                    background: 'linear-gradient(transparent, rgba(0,0,0,0.3))',
                                }} />
                            </Box>
                        )}

                        {/* Blog Content */}
                        <Box sx={{ 
                            p: { xs: 3, sm: 4, md: 6 },
                            position: 'relative',
                            zIndex: 1,
                        }}>
                            {/* Blog Title */}
                            <Typography 
                                variant="h3" 
                                sx={{ 
                                    fontWeight: 900,
                                    color: "#2c2c2c",
                                    mb: 4,
                                    fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.8rem' },
                                    lineHeight: 1.2,
                                    textShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    letterSpacing: '-0.02em',
                                    textAlign: 'center',
                                    animation: 'slideInLeft 1s ease-out 0.3s both',
                                }}
                            >
                                {blogsData?.title}
                            </Typography>

                            {/* Blog Content Container */}
                            <Box sx={{ 
                                '& p': {
                                    color: '#333333',
                                    fontSize: { xs: '1.2rem', sm: '1.3rem', md: '1.4rem' },
                                    lineHeight: 1.8,
                                    mb: 3,
                                    fontWeight: 400,
                                },
                                '& h1, & h2, & h3, & h4, & h5, & h6': {
                                    color: '#2c2c2c',
                                    fontWeight: 700,
                                    mb: 3,
                                    mt: 4,
                                    '&': {
                                        fontSize: { xs: '1.6rem', sm: '1.8rem', md: '2rem' },
                                    },
                                },
                                '& h1': {
                                    fontSize: { xs: '2rem', sm: '2.4rem', md: '2.8rem' },
                                },
                                '& h2': {
                                    fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.4rem' },
                                },
                                '& h3': {
                                    fontSize: { xs: '1.6rem', sm: '1.9rem', md: '2.1rem' },
                                },
                                '& h4': {
                                    fontSize: { xs: '1.4rem', sm: '1.7rem', md: '1.9rem' },
                                },
                                '& a': {
                                    color: '#667eea !important',
                                    textDecoration: 'none !important',
                                    fontWeight: 600,
                                    fontSize: { xs: '1.2rem', sm: '1.3rem', md: '1.4rem' },
                                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(102, 126, 234, 0.05) 100%)',
                                    padding: '4px 10px',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(102, 126, 234, 0.2)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(102, 126, 234, 0.1) 100%)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)',
                                    },
                                },
                                '& ul, & ol': {
                                    color: '#333333',
                                    pl: 3,
                                    '& li': {
                                        mb: 2,
                                        fontSize: { xs: '1.2rem', sm: '1.3rem', md: '1.4rem' },
                                        lineHeight: 1.7,
                                    },
                                },
                                '& blockquote': {
                                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(102, 126, 234, 0.05) 100%)',
                                    backdropFilter: 'blur(15px)',
                                    border: 'none',
                                    borderLeft: '4px solid #667eea',
                                    borderRadius: '15px',
                                    p: 4,
                                    my: 4,
                                    fontStyle: 'italic',
                                    color: '#2c2c2c',
                                    fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' },
                                },
                                '& code': {
                                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(102, 126, 234, 0.05) 100%)',
                                    color: '#667eea',
                                    padding: '6px 10px',
                                    borderRadius: '8px',
                                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                                    fontFamily: 'monospace',
                                    fontWeight: 600,
                                },
                                '& pre': {
                                    background: 'linear-gradient(135deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.02) 100%)',
                                    backdropFilter: 'blur(15px)',
                                    border: '1px solid rgba(0,0,0,0.1)',
                                    borderRadius: '15px',
                                    p: 4,
                                    my: 4,
                                    overflow: 'auto',
                                    '& code': {
                                        background: 'transparent',
                                        color: '#2c2c2c',
                                        fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                                    },
                                },
                                animation: 'fadeInUp 1s ease-out 0.6s both',
                            }}>
                                {parsedContent}
                            </Box>
                        </Box>
                    </Box>
                </Container>
            </Box>
        </React.Fragment>

    );
};

export default BlogSection;
